import React, { useMemo, useCallback, memo } from 'react';
import { BarChart, ArrowUp, ArrowDown, Info } from 'lucide-react';

// Separate Star component to avoid recreating it on each render
const Star = memo((props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
));

Star.displayName = 'Star';

// Memoized Tooltip component to prevent unnecessary rerenders
const BarTooltip = memo(({ day, share, reputation }) => (
  <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap z-20 shadow-lg">
    <div className="font-medium">Day {day}</div>
    <div className="flex items-center text-blue-300">
      <BarChart className="w-3 h-3 mr-1" />
      Market Share: {share.toFixed(1)}%
    </div>
    {reputation !== undefined && (
      <div className="flex items-center text-yellow-300">
        <Star className="w-3 h-3 mr-1" />
        Reputation: {reputation.toFixed(2)}
      </div>
    )}
  </div>
));

BarTooltip.displayName = 'BarTooltip';

// Memoized Bar component to prevent unnecessary rerenders
const Bar = memo(({ data, index, normalized, isHighlighted, barWidth }) => (
  <div className="flex-1 flex flex-col items-center h-full group">
    <div className="relative h-full flex items-end w-full transition-all duration-200">
      {/* Tooltip */}
      <BarTooltip 
        day={data.day} 
        share={data.share} 
        reputation={data.reputation}
      />
      
      {/* Bar with dot on top */}
      <div className="relative">
        <div
          className={`${barWidth} mx-auto rounded-t transition-all duration-200 group-hover:w-full 
          ${isHighlighted ? 'bg-blue-600' : 'bg-blue-500'} 
          group-hover:bg-blue-600`}
          style={{ height: `${normalized}%` }}
        />
        
        {/* Dot indicator at the top of each bar */}
        <div 
          className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full 
          ${isHighlighted ? 'bg-blue-700' : 'bg-blue-600'}`}
        />
      </div>
      
      {/* X-axis label */}
      <div className={`absolute bottom-0 w-full text-center -mb-6 text-xs transition-all duration-200 
        ${isHighlighted ? 'opacity-100 font-medium' : 'opacity-70 group-hover:opacity-100'}`}>
        Day {data.day}
      </div>
    </div>
  </div>
));

Bar.displayName = 'Bar';

// Determine which points to highlight 
const makeHighlightIndices = (dataArray) => {
  if (dataArray.length <= 7) {
    // For small datasets, highlight first, middle and last points
    const indices = [0];
    if (dataArray.length > 2) {
      indices.push(Math.floor(dataArray.length / 2));
    }
    if (dataArray.length > 1) {
      indices.push(dataArray.length - 1);
    }
    return indices;
  }
  
  // For larger datasets, highlight strategic points
  const lastIndex = dataArray.length - 1;
  return [
    0, // First point
    Math.floor(lastIndex / 4), // Quarter
    Math.floor(lastIndex / 2), // Middle
    Math.floor(lastIndex * 3 / 4), // Three-quarters
    lastIndex // Last point
  ];
};

const MarketShareGraph = ({ data }) => {
  // Process data once on component mount or when data changes
  const {
    processedData,
    marketShare,
    previousShare,
    isIncreasing,
    changeAmount,
    minShare,
    maxShare,
    highlightIndices,
    barWidth
  } = useMemo(() => {
    // Ensure we have data and handle edge cases
    const marketData = Array.isArray(data) && data.length > 0 ? data : [];
    
    // Filter out any interpolated data points to only show actual data
    const processedData = marketData.filter(item => !item.isInterpolated);
    
    // Get current and previous values for trend calculation
    const marketShare = processedData.length > 0 ? processedData[processedData.length - 1].share : 0;
    const previousShare = processedData.length > 1 ? processedData[processedData.length - 2].share : marketShare;
    const isIncreasing = marketShare >= previousShare;
    const changeAmount = Math.abs(marketShare - previousShare).toFixed(1);
    
    // Calculate min and max for better scaling
    const shareValues = processedData.map(d => d.share).filter(v => v !== undefined && v !== null);
    
    // Set more appropriate min/max values to improve readability
    let minShare = shareValues.length > 0 ? Math.min(...shareValues) : 0;
    let maxShare = shareValues.length > 0 ? Math.max(...shareValues) : 100;
    
    // Add padding to make the graph more readable (10% padding)
    const range = maxShare - minShare;
    minShare = Math.max(0, minShare - range * 0.1); // Don't go below 0
    maxShare = maxShare + range * 0.1;
    
    // Determine which points to highlight
    const highlightIndices = makeHighlightIndices(processedData);

    // Determine bar width based on number of data points
    const barWidth = processedData.length > 20 ? 'w-1/2' : processedData.length > 10 ? 'w-3/5' : 'w-2/3';
    
    return {
      processedData,
      marketShare,
      previousShare,
      isIncreasing,
      changeAmount,
      minShare,
      maxShare,
      highlightIndices,
      barWidth
    };
  }, [data]);
  
  // Memoize the normalize height function to prevent recalculating on every render
  const normalizeHeight = useCallback((value) => {
    if (minShare === maxShare) return 50; // Handle case where all values are the same
    return ((value - minShare) / (maxShare - minShare)) * 90 + 10; // Keep bars between 10-100% height
  }, [minShare, maxShare]);
  
  // Generate Y-axis labels
  const yAxisLabels = useMemo(() => {
    const range = maxShare - minShare;
    return [
      maxShare.toFixed(1),
      (minShare + (range * 0.75)).toFixed(1),
      (minShare + (range * 0.5)).toFixed(1),
      (minShare + (range * 0.25)).toFixed(1),
      minShare.toFixed(1)
    ];
  }, [minShare, maxShare]);

  // If we have no data, show a placeholder message
  if (!processedData.length) {
    return (
      <div className="mb-4 p-6 bg-white border rounded-lg shadow flex justify-center items-center h-64">
        <p className="text-gray-500">No market share data available</p>
      </div>
    );
  }

  return (
    <div className="mb-4 p-6 bg-white border rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-600" />
          Market Share Trend
        </h3>
        
        <div className="flex items-center">
          <div className="px-3 py-1 rounded-full bg-gray-100 flex items-center mr-2">
            <span className="text-sm font-medium mr-1">Current: {marketShare.toFixed(1)}%</span>
            <div className={`flex items-center ${isIncreasing ? 'text-green-600' : 'text-red-600'}`}>
              {isIncreasing ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              <span className="text-xs font-bold">{changeAmount}%</span>
            </div>
          </div>
          
          <button className="text-gray-400 hover:text-gray-600" aria-label="More information">
            <Info className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="h-64 flex items-end relative bg-gray-50 rounded-lg p-2">
        {/* Y-axis labels */}
        <div className="absolute inset-y-0 left-0 w-10 flex flex-col justify-between text-xs text-gray-500 py-2">
          {yAxisLabels.map((label, i) => (
            <span key={i}>{label}%</span>
          ))}
        </div>
        
        {/* Gridlines */}
        <div className="absolute inset-0 ml-10 flex flex-col justify-between pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-px bg-gray-200 w-full"></div>
          ))}
        </div>
        
        {/* Chart area */}
        <div className="ml-10 flex-1 flex items-end">
          {processedData.map((data, index) => {
            const isHighlighted = highlightIndices.includes(index);
            const normalized = normalizeHeight(data.share);
            
            return (
              <Bar
                key={`${data.day}-${index}`}
                data={data}
                index={index}
                normalized={normalized}
                isHighlighted={isHighlighted}
                barWidth={barWidth}
              />
            );
          })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap mt-8 pt-2 border-t">
        <div className="flex items-center text-sm mr-6">
          <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
          <span className="text-gray-600">Highlighted Data Points</span>
        </div>
        <div className="flex items-center text-sm">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Standard Data Points</span>
        </div>
      </div>
    </div>
  );
};

export default memo(MarketShareGraph);